import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, X, MapPin, Trophy, Shield, Flame, Sparkles, Users as UsersIcon } from "lucide-react";
import api, { sportInfo } from "../lib/api";
import { useAuth } from "../lib/auth";
import PageShell from "../components/PageShell";
import BottomNav from "../components/BottomNav";
import MatchCard from "../components/MatchCard";
import EmptyState from "../components/EmptyState";
import { Section } from "../components/Section";

const TABS = [
  { id: "all", label: "Semua" },
  { id: "matches", label: "Matches" },
  { id: "teams", label: "Teams" },
  { id: "players", label: "Players" },
];

export default function Search() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  // Discovery state (when q is empty)
  const [trending, setTrending] = useState([]);
  const [needOne, setNeedOne] = useState([]);
  const [tonight, setTonight] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [beginner, setBeginner] = useState([]);
  const [popularTeams, setPopularTeams] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);

  // Search results (when q is non-empty)
  const [matchResults, setMatchResults] = useState([]);
  const [teamResults, setTeamResults] = useState([]);
  const [playerResults, setPlayerResults] = useState([]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Load discovery sections (only when no query)
  const loadDiscovery = useCallback(async () => {
    setLoading(true);
    try {
      const params = user?.city ? { city: user.city } : {};
      const [matchesRes, trendRes, teamsRes, playersRes] = await Promise.all([
        api.get("/matches", { params }),
        api.get("/matches/trending-sports"),
        api.get("/teams/popular", { params: { limit: 8 } }),
        api.get("/users", { params: { limit: 12 } }),
      ]);
      const all = matchesRes.data;
      const now = new Date();
      const endOfToday = new Date(now); endOfToday.setHours(23, 59, 59, 999);
      setTrending(trendRes.data);
      setNeedOne(all.filter(m => (m.max_players - (m.player_ids?.length || 0)) === 1 && m.status !== "FULL").slice(0, 6));
      setTonight(all.filter(m => { const d = new Date(m.starts_at); return d >= now && d <= endOfToday; }).slice(0, 6));
      setNearby(all.slice(0, 8));
      setBeginner(all.filter(m => m.skill_level === "beginner" || m.skill_level === "all").slice(0, 6));
      setPopularTeams(teamsRes.data);
      setActivePlayers(playersRes.data);
    } catch (err) {
      console.warn("[search] discovery load failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [user?.city]);

  // Load search results when query is set
  const loadResults = useCallback(async (term) => {
    setLoading(true);
    try {
      const [m, t, p] = await Promise.all([
        api.get("/matches", { params: { q: term } }),
        api.get("/teams", { params: { q: term } }),
        api.get("/users", { params: { q: term, limit: 24 } }),
      ]);
      setMatchResults(m.data);
      setTeamResults(t.data);
      setPlayerResults(p.data);
    } catch (err) {
      console.warn("[search] results load failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedQ) loadResults(debouncedQ);
    else loadDiscovery();
  }, [debouncedQ, loadResults, loadDiscovery]);

  const isSearching = debouncedQ.length > 0;

  // Visible groups depend on tab — applies to both discovery sections AND search results
  const showMatches = tab === "all" || tab === "matches";
  const showTeams = tab === "all" || tab === "teams";
  const showPlayers = tab === "all" || tab === "players";

  const totalResults = useMemo(
    () => matchResults.length + teamResults.length + playerResults.length,
    [matchResults, teamResults, playerResults]
  );

  return (
    <PageShell>
      {/* Hero */}
      <div className="px-4 pt-6 pb-3">
        <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Discover</div>
        <h1 className="font-heading text-3xl font-black tracking-tight mt-1">
          <span className="text-foreground">Jelajah</span> <span className="text-primary">JoinPlay</span>
        </h1>
        <div className="mt-1 text-sm text-muted-foreground">Cari match, team, atau pemain di seluruh kota.</div>
      </div>

      {/* Sticky search + tabs */}
      <div className="sticky top-0 z-20 bg-[#09090B]/95 backdrop-blur-md px-4 pb-3 pt-1">
        <label className="flex items-center gap-2 bg-card border border-border rounded-full px-4 py-3 focus-within:border-primary/60 transition-colors">
          <SearchIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari match, team, atau player…"
            className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
            inputMode="search"
            autoComplete="off"
          />
          {q && (
            <button
              data-testid="search-clear-btn"
              onClick={() => setQ("")}
              className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center text-muted-foreground"
              aria-label="Clear"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </label>

        <div className="mt-3 flex gap-1.5 overflow-x-auto scrollbar-hide">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`search-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground border border-border hover:text-foreground"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {isSearching ? (
        // -------------- SEARCH RESULTS --------------
        <>
          <div className="px-4 mt-2 text-xs text-muted-foreground" data-testid="search-result-count">
            {loading ? "Mencari…" : `${totalResults} hasil untuk "${debouncedQ}"`}
          </div>

          {!loading && totalResults === 0 && (
            <div className="mt-10 px-4">
              <EmptyState icon="🧭" title="Belum ada hasil" body="Coba kata kunci lain, atau cek kota lo." />
            </div>
          )}

          {showMatches && matchResults.length > 0 && (
            <Section title="Matches" subtitle={`${matchResults.length} ditemukan`} testid="search-matches">
              <div className="px-4 space-y-3">
                {matchResults.map((m) => <MatchCard key={m.match_id} match={m} />)}
              </div>
            </Section>
          )}

          {showTeams && teamResults.length > 0 && (
            <Section title="Teams" subtitle={`${teamResults.length} crew`} testid="search-teams">
              <div className="px-4 space-y-3">
                {teamResults.map((t) => <TeamResultCard key={t.team_id} team={t} />)}
              </div>
            </Section>
          )}

          {showPlayers && playerResults.length > 0 && (
            <Section title="Players" subtitle={`${playerResults.length} pemain`} testid="search-players">
              <div className="px-4 space-y-3">
                {playerResults.map((p) => <PlayerResultCard key={p.user_id} player={p} />)}
              </div>
            </Section>
          )}
        </>
      ) : (
        // -------------- DISCOVERY HUB --------------
        <>
          {/* Trending sports chips */}
          {showMatches && trending.length > 0 && (
            <Section title="Trending Sports" subtitle="Olahraga paling aktif" icon={Flame} testid="disc-trending">
              <div className="px-4 flex gap-2 overflow-x-auto scrollbar-hide">
                {trending.map((t) => {
                  const s = sportInfo(t.sport);
                  return (
                    <button
                      key={t.sport}
                      onClick={() => setQ(s.label)}
                      data-testid={`disc-trending-${t.sport}`}
                      className="shrink-0 bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-2 hover:border-primary/40"
                    >
                      <span className="text-xl">{s.icon}</span>
                      <div className="text-left">
                        <div className="text-sm font-bold">{s.label}</div>
                        <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">{t.count} aktif</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Section>
          )}

          {showMatches && needOne.length > 0 && (
            <Section title="Butuh 1 Pemain Lagi" subtitle="Slot terakhir, gaskeun" icon={Sparkles} testid="disc-need-one">
              <div className="px-4 flex gap-3 overflow-x-auto scrollbar-hide">
                {needOne.map((m) => <div key={m.match_id} className="shrink-0 w-[300px]"><MatchCard match={m} compact /></div>)}
              </div>
            </Section>
          )}

          {showMatches && tonight.length > 0 && (
            <Section title="Main Malam Ini" subtitle="Match yang lagi gaskeun hari ini" testid="disc-tonight">
              <div className="px-4 space-y-3">
                {tonight.map((m) => <MatchCard key={m.match_id} match={m} />)}
              </div>
            </Section>
          )}

          {showMatches && nearby.length > 0 && (
            <Section title={user?.city ? `Dekat Lo · ${user.city}` : "Nearby Matches"} subtitle="Match terdekat" testid="disc-nearby">
              <div className="px-4 flex gap-3 overflow-x-auto scrollbar-hide">
                {nearby.map((m) => <div key={m.match_id} className="shrink-0 w-[300px]"><MatchCard match={m} compact /></div>)}
              </div>
            </Section>
          )}

          {showMatches && beginner.length > 0 && (
            <Section title="Beginner Friendly" subtitle="Cocok buat yang baru mulai" testid="disc-beginner">
              <div className="px-4 space-y-3">
                {beginner.map((m) => <MatchCard key={m.match_id} match={m} />)}
              </div>
            </Section>
          )}

          {showTeams && popularTeams.length > 0 && (
            <Section title="Crews Populer" subtitle="Team aktif di JoinPlay" icon={UsersIcon} testid="disc-popular-teams">
              <div className="px-4 grid grid-cols-2 gap-3">
                {popularTeams.map((t) => <PopularTeamCard key={t.team_id} team={t} />)}
              </div>
            </Section>
          )}

          {showPlayers && activePlayers.length > 0 && (
            <Section title="Pemain Aktif" subtitle="Reliability & jam terbang tinggi" icon={Trophy} testid="disc-active-players">
              <div className="px-4 flex gap-3 overflow-x-auto scrollbar-hide">
                {activePlayers.map((p) => <ActivePlayerCard key={p.user_id} player={p} />)}
              </div>
            </Section>
          )}

          {!loading && (
            (tab === "all" && nearby.length === 0 && popularTeams.length === 0 && activePlayers.length === 0) ||
            (tab === "matches" && nearby.length === 0 && needOne.length === 0 && tonight.length === 0 && beginner.length === 0) ||
            (tab === "teams" && popularTeams.length === 0) ||
            (tab === "players" && activePlayers.length === 0)
          ) && (
            <div className="mt-10 px-4">
              <EmptyState
                icon="🛰️"
                title={
                  tab === "teams" ? "Belum ada crew populer"
                  : tab === "players" ? "Belum ada pemain aktif"
                  : tab === "matches" ? "Belum ada match"
                  : "JoinPlay masih sepi di area lo"
                }
                body={tab === "all" ? "Jadi yang pertama buat match!" : "Coba kembali nanti, atau ganti tab."}
              />
            </div>
          )}
        </>
      )}

      <div className="h-32" aria-hidden />
      <BottomNav />
    </PageShell>
  );
}

function TeamResultCard({ team }) {
  const s = sportInfo(team.sport);
  return (
    <Link
      to={`/teams/${team.team_id}`}
      data-testid={`team-result-${team.team_id}`}
      className="block bg-card border border-border rounded-2xl p-4 flex items-center gap-3 hover:border-primary/40 transition-colors"
    >
      <div className="h-12 w-12 rounded-2xl bg-secondary flex items-center justify-center overflow-hidden shrink-0 border border-border">
        {team.logo ? <img src={team.logo} alt="" className="w-full h-full object-cover" /> : <span className="text-xl">{s.icon}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate">{team.name}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
          <span>{s.label}</span>·<span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{team.city}</span>
        </div>
        {typeof team.member_count === "number" && (
          <div className="text-[10px] mt-1 font-bold uppercase tracking-wider text-primary">{team.member_count} anggota</div>
        )}
      </div>
    </Link>
  );
}

function PopularTeamCard({ team }) {
  const s = sportInfo(team.sport);
  return (
    <Link
      to={`/teams/${team.team_id}`}
      data-testid={`popular-team-${team.team_id}`}
      className="bg-card border border-border rounded-2xl p-3 hover:border-primary/40 transition-colors flex flex-col gap-2"
    >
      <div className="flex items-center gap-2">
        <div className="h-10 w-10 rounded-2xl bg-secondary flex items-center justify-center overflow-hidden shrink-0 border border-border">
          {team.logo ? <img src={team.logo} alt="" className="w-full h-full object-cover" /> : <span className="text-lg">{s.icon}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm truncate">{team.name}</div>
          <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider truncate">{s.label} · {team.city}</div>
        </div>
      </div>
      {typeof team.member_count === "number" && (
        <div className="text-[10px] font-bold uppercase tracking-wider text-primary">{team.member_count} member</div>
      )}
    </Link>
  );
}

function PlayerResultCard({ player }) {
  const sports = (player.sports || []).slice(0, 3).map((sid) => sportInfo(sid));
  return (
    <Link
      to={`/profile`}
      onClick={(e) => e.preventDefault()}
      data-testid={`player-result-${player.user_id}`}
      className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3 hover:border-primary/40 transition-colors cursor-default"
    >
      <div className="h-12 w-12 rounded-full overflow-hidden bg-secondary border border-border shrink-0">
        {player.picture ? <img src={player.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold">{(player.name || "?")[0]}</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate">{player.name}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-1 truncate mt-0.5">
          {player.city && <><MapPin className="h-3 w-3" />{player.city}</>}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {sports.map((s) => (
            <span key={s.id} className="text-[10px] font-bold uppercase tracking-wider bg-secondary border border-border rounded-full px-1.5 py-0.5">{s.icon} {s.label}</span>
          ))}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-heading text-base font-black text-primary leading-none">{Number(player.reliability_score ?? 100).toFixed(0)}%</div>
        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1 mt-1"><Shield className="h-2.5 w-2.5" />REL</div>
      </div>
    </Link>
  );
}

function ActivePlayerCard({ player }) {
  return (
    <div
      data-testid={`active-player-${player.user_id}`}
      className="shrink-0 w-[180px] bg-card border border-border rounded-2xl p-4 flex flex-col items-center text-center"
    >
      <div className="h-16 w-16 rounded-full overflow-hidden bg-secondary border-2 border-primary/40 glow-cyan">
        {player.picture ? <img src={player.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-xl">{(player.name || "?")[0]}</div>}
      </div>
      <div className="mt-2 font-bold text-sm truncate w-full">{player.name}</div>
      {player.city && <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider truncate w-full">{player.city}</div>}
      <div className="mt-2 flex items-center gap-2">
        <div className="text-center">
          <div className="font-heading text-sm font-black text-primary leading-none">{player.matches_played ?? 0}</div>
          <div className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground">MATCH</div>
        </div>
        <div className="h-6 w-px bg-border" />
        <div className="text-center">
          <div className="font-heading text-sm font-black text-primary leading-none">{Number(player.reliability_score ?? 100).toFixed(0)}%</div>
          <div className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground">REL</div>
        </div>
      </div>
    </div>
  );
}
